import React from 'react';
import styled from 'styled-components';
import IconBlock from '@component/common/block/icon-block';
import IconTextBlock from '@component/common/block/icon-text-block';
import TextBlock from '@component/common/block/text-block';
import Gap from '@component/common/gap';
import type { IPlaybackState } from '@component/common/otter-teacher';
import * as color from '@constant/color';
import * as size from '@constant/size';

interface IProps {
  audioRef: React.RefObject<HTMLAudioElement | null>;
  playback: IPlaybackState;
}

const SKIP_SECONDS = 10;
const PLAYBACK_RATES = [1, 1.5, 2] as const;

const Wrapper = styled.div`
  display: flex;
  flex-flow: column nowrap;
  align-items: center;
  width: 100%;
`;

const SecondRowContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 15px;
  background-color: ${color.GRAY_3};
  border-radius: ${size.BORDER_RADIUS_3};
`;

const ControlContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  // padding: 5px 10px;
  gap: ${size.MARGIN_1};
  // background-color: ${color.GRAY_3};
  // border-radius: ${size.BORDER_RADIUS_3};
`;

const SeekBar = styled.input`
  width: 100%;
  height: 6px;
  cursor: pointer;
  accent-color: ${color.BROWN_1};
`;

// 시간 표시는 자릿수 흔들림 방지(tabular-nums) + 고정 폭. 텍스트 자체는 TextBlock으로 렌더.
const TimeLabel = styled.div`
  min-width: 78px;
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

// 초 → m:ss
const formatTime = (seconds: number) => {
  if (!Number.isFinite(seconds) || seconds < 0) return '0:00';
  const total = Math.floor(seconds);
  const minutes = Math.floor(total / 60);
  const secs = total % 60;
  return `${minutes}:${secs.toString().padStart(2, '0')}`;
};

// 강의 컨트롤 바 — audio를 직접 제어(seek/skip/toggle). board(Remotion)는 playback 구독으로 자동 동기화.
const LectureControl: React.FC<IProps> = ({ audioRef, playback }) => {
  const { currentTime, duration, isPlaying, playbackRate } = playback;

  const handleTogglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;
    if (audio.paused) void audio.play().catch(() => undefined);
    else audio.pause();
  };

  const handleSkip = (deltaSeconds: number) => {
    const audio = audioRef.current;
    if (!audio) return;
    const max = Number.isFinite(audio.duration) ? audio.duration : 0;
    audio.currentTime = Math.min(Math.max(audio.currentTime + deltaSeconds, 0), max);
  };

  const handleSeek = (event: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;
    audio.currentTime = Number(event.target.value);
  };

  const handlePlaybackRateChange = () => {
    const audio = audioRef.current;
    if (!audio) return;
    const currentRateIndex = PLAYBACK_RATES.indexOf(audio.playbackRate as (typeof PLAYBACK_RATES)[number]);
    const nextPlaybackRate = PLAYBACK_RATES[(currentRateIndex + 1) % PLAYBACK_RATES.length];
    audio.playbackRate = nextPlaybackRate;
  };

  // play/pause는 채운 아이콘, 겹화살표(rewind/fast-forward)는 선형이라 outlined
  const skipDesign = {
    icon: { size: 'medium', kind: 'outlined' },
    box: { event: { cursor: 'pointer' } },
  } as const;
  const playDesign = {
    icon: { size: 'medium', kind: 'filled' },
    box: { event: { cursor: 'pointer' } },
  } as const;

  return (
    <Wrapper>
      <SeekBar
        type='range'
        min={0}
        max={duration || 0}
        step={0.1}
        value={currentTime}
        onChange={handleSeek}
        aria-label='강의 진행바'
      />
      <Gap height={size.MARGIN_1} />
      <SecondRowContainer>
        <ControlContainer>
          <IconBlock icon='rewind' design={skipDesign} click={{ handler: () => handleSkip(-SKIP_SECONDS) }} />
          <IconBlock icon={isPlaying ? 'pause' : 'play'} design={playDesign} click={{ handler: handleTogglePlay }} />
          <IconBlock icon='fast-forward' design={skipDesign} click={{ handler: () => handleSkip(SKIP_SECONDS) }} />
          <Gap width={size.MARGIN_1} />
          <IconTextBlock
            icon={playbackRate === 2 ? 'more' : 'less'}
            text={`${playbackRate.toFixed(1)}x`}
            arrangement='icon-text'
            design={{
              icon: { size: 'small', color: color.BLACK_4, kind: 'outlined' },
              text: { size: 'medium', color: color.BLACK_4, bold: false },
              box: {
                gap: '2px',
                padding: '2px 10px 2px 5px',
                backgroundColor: color.WHITE_1,
                borderWidth: size.BORDER_WIDTH_1,
                borderColor: color.BLACK_4,
                borderRadius: size.BORDER_RADIUS_2,
                event: { cursor: 'pointer' },
              },
            }}
            click={{ handler: handlePlaybackRateChange }}
          />
        </ControlContainer>
        <TimeLabel>
          <TextBlock
            text={`${formatTime(currentTime)} / ${formatTime(duration)}`}
            design={{ text: { size: 'small', color: color.BLACK_2 } }}
          />
        </TimeLabel>
      </SecondRowContainer>
    </Wrapper>
  );
};

export default LectureControl;
